import { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ShoppingCart, Utensils, Wine, GlassWater, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const courseMeta = {
  meal: { icon: Utensils, accent: "#E50914", label: "Meal" },
  drink: { icon: Wine, accent: "#F5B841", label: "Cocktail" },
  mocktail: { icon: GlassWater, accent: "#5EC5A0", label: "Mocktail" },
};

function buildList(p) {
  const raw = [
    ...(p.meal_ingredients || []).map((t) => ({ text: t, course: "meal" })),
    ...(p.drink_ingredients || []).map((t) => ({ text: t, course: "drink" })),
    ...(p.non_alcoholic_drink_ingredients || []).map((t) => ({ text: t, course: "mocktail" })),
  ];
  // combine exact duplicates (case-insensitive), keep first course
  const seen = new Map();
  raw.forEach((item) => {
    const key = item.text.trim().toLowerCase();
    if (!seen.has(key)) seen.set(key, item);
  });
  return Array.from(seen.values());
}

export const ShoppingList = ({ pairing, open, onClose }) => {
  const items = useMemo(() => (pairing ? buildList(pairing) : []), [pairing]);
  const storeKey = pairing ? `dm_shopping_${pairing.id || pairing.movie_title}` : null;
  const [checked, setChecked] = useState([]);

  useEffect(() => {
    if (!open || !storeKey) return;
    try {
      setChecked(JSON.parse(localStorage.getItem(storeKey) || "[]"));
    } catch {
      setChecked([]);
    }
  }, [open, storeKey]);

  const persist = (next) => {
    setChecked(next);
    if (storeKey) localStorage.setItem(storeKey, JSON.stringify(next));
  };

  const toggle = (text) => {
    persist(checked.includes(text) ? checked.filter((t) => t !== text) : [...checked, text]);
  };

  const clearAll = () => persist([]);

  const copyList = async () => {
    const text = items.map((i) => `- ${i.text}`).join("\n");
    try {
      await navigator.clipboard.writeText(`Shopping List for ${pairing.movie_title}\n${text}`);
      toast.success("Shopping list copied!");
    } catch {
      toast.message("Could not copy");
    }
  };

  if (!pairing) return null;
  const doneCount = items.filter((i) => checked.includes(i.text)).length;
  const progress = items.length ? (doneCount / items.length) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 max-w-lg p-0 gap-0" data-testid="shopping-list-dialog">
        <DialogHeader className="p-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#E50914]" />
            <DialogTitle className="font-display text-2xl">Shopping List</DialogTitle>
          </div>
          <p className="text-sm text-white/50">
            Everything you need for <span className="text-white/80">{pairing.movie_title}</span>
          </p>
          <DialogDescription className="sr-only">
            A combined checklist of every ingredient for this pairing.
          </DialogDescription>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-white/50 mb-1.5">
              <span data-testid="shopping-progress">{doneCount} of {items.length} gathered</span>
              {doneCount === items.length && items.length > 0 && (
                <span className="text-[#5EC5A0]">All set!</span>
              )}
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#5EC5A0] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[50vh] overflow-y-auto custom-scroll p-4">
          <ul className="space-y-1.5">
            {items.map((item, idx) => {
              const isChecked = checked.includes(item.text);
              const meta = courseMeta[item.course];
              return (
                <li key={`${idx}-${item.text}`}>
                  <button
                    onClick={() => toggle(item.text)}
                    data-testid={`shopping-item-${idx}`}
                    className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-white/5 transition-colors group"
                  >
                    <span
                      className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                        isChecked ? "bg-[#5EC5A0] border-[#5EC5A0]" : "border-white/25 group-hover:border-white/50"
                      }`}
                    >
                      {isChecked && <Check className="w-3.5 h-3.5 text-black" />}
                    </span>
                    <span
                      className={`flex-1 text-sm transition-all ${
                        isChecked ? "text-white/35 line-through" : "text-white/85"
                      }`}
                    >
                      {item.text}
                    </span>
                    <span
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                      style={{ color: meta.accent, backgroundColor: `${meta.accent}18` }}
                    >
                      {meta.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-white/10">
          <Button
            onClick={clearAll}
            variant="ghost"
            data-testid="shopping-clear-btn"
            className="rounded-full bg-white/5 hover:bg-white/10 text-white/70 h-10 px-4 text-sm"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <Button
            onClick={copyList}
            data-testid="shopping-copy-btn"
            className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-10 px-5 text-sm"
          >
            <Copy className="w-4 h-4 mr-1.5" /> Copy List
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
